import { fetchVenueRecordCached } from '../utils/venueRecordCache';
import { touchVenueDashboardActivity } from '../utils/venueDashboardSession';
import {
  getCachedAvailabilityBlocks,
  setCachedAvailabilityBlocks,
  getCachedAvailabilityPreview,
  setCachedAvailabilityPreview
} from '../utils/availabilityCache';
import { hostPhotoFilenameFromStoredUrl } from '../utils/hostPhotoUrl';
import { logInternalError, toCustomerError } from '../utils/customerError';

import { API_BASE_URL } from '../config/apiEndpoints';

const INVOICE_CONTENT_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
]);

const INVOICE_EXTENSION_CONTENT_TYPES = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
};

/** Resolve invoice MIME type from File metadata (handles empty browser file.type). */
export const resolveInvoiceFileContentType = (file) => {
  if (!file) return null;
  const direct = String(file.type || '').trim().toLowerCase();
  if (direct && INVOICE_CONTENT_TYPES.has(direct)) return direct;
  const ext = String(file.name || '')
    .split('.')
    .pop()
    ?.toLowerCase();
  return INVOICE_EXTENSION_CONTENT_TYPES[ext] || null;
};

const catalogArrayFromPayload = (data, keys = []) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  for (const key of keys) {
    if (Array.isArray(data[key])) return data[key];
  }
  return [];
};

const putInvoiceFileToPresignedUrl = async (uploadUrl, file) => {
  // Presigned URLs are generated without Content-Type in the signature — omit headers first.
  let uploadResponse = await fetch(uploadUrl, { method: 'PUT', body: file });
  if (uploadResponse.ok) return;

  const contentType = resolveInvoiceFileContentType(file);
  if (contentType) {
    uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file
    });
  }

  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload invoice file (${uploadResponse.status})`);
  }
};

const shouldFallbackToPresignedInvoiceUpload = (error) => {
  const msg = String(error?.message || '');
  if (!msg) return false;
  if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) return true;
  return /\((404|405|501|503)\)/.test(msg);
};

/**
 * Service for venue dashboard API calls
 */
class VenueDashboardApiService {
  getAuthHeaders() {
    touchVenueDashboardActivity();
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async parseError(response) {
    let detail = '';
    try {
      const errorData = await response.json();
      detail = errorData.detail || errorData.message || errorData.error || '';
      if (typeof detail === 'object' && detail !== null) {
        detail = detail.message ? String(detail.message) : '';
      }
    } catch (e) {
      // Response isn't JSON
    }
    const detailStr = typeof detail === 'string' ? detail.trim() : '';
    logInternalError('Venue dashboard API error', {
      status: response.status,
      url: response.url,
      detail: detailStr || detail
    });
    if (response.status === 401) {
      return 'You are not authorised. Please log in again.';
    }
    if (response.status === 400) {
      return toCustomerError(detailStr, {
        status: 400,
        fallback: 'Invalid request. Please check your input and try again.'
      });
    }
    return toCustomerError(detailStr, { status: response.status });
  }

  async parseJsonBody(response) {
    const text = await response.text();
    if (!text || !String(text).trim()) return {};
    try {
      return JSON.parse(text);
    } catch {
      return {};
    }
  }

  async fetchWithFallback(urls, options = {}, { allowEmpty = false } = {}) {
    let lastError = null;
    for (const url of urls) {
      const response = await fetch(url, options);
      if (response.ok) {
        return await response.json();
      }
      if (response.status === 404) {
        lastError = new Error(toCustomerError('', { status: 404 }));
        continue;
      }
      const errorMessage = await this.parseError(response);
      throw new Error(errorMessage);
    }
    if (allowEmpty) {
      return [];
    }
    if (lastError) {
      throw lastError;
    }
    throw new Error('No endpoints available');
  }
  /**
   * Get all dashboard data for a venue
   * @param {string} venueId - Venue ID
   * @returns {Promise<Object>} All dashboard data
   */
  async getAllDashboardData(venueId) {
    try {
      const [operatingHours, holidays, dynamicPricing] = await Promise.all([
        this.getOperatingHours(venueId),
        this.getHolidays(venueId),
        this.getDynamicPricing(venueId)
      ]);

      return {
        operatingHours,
        holidays,
        dynamicPricing
      };
    } catch (error) {

      throw error;
    }
  }

  /**
   * Get operating hours for a venue
   * @param {string} venueId - Venue ID
   * @returns {Promise<Object>} Operating hours data
   */
  transformOperatingHoursList(data) {
    const operatingHoursObject = {};
    if (!Array.isArray(data)) return operatingHoursObject;
    data.forEach((item) => {
      const dayNumber = item.day_of_week === 0 ? 7 : item.day_of_week;
      operatingHoursObject[dayNumber] = {
        is_open: item.is_open || false,
        open_time: item.open_time || '10:00:00',
        close_time: item.close_time || '23:00:00'
      };
    });
    return operatingHoursObject;
  }

  /**
   * Owner dashboard bundle — one request for venue config (database-api).
   */
  async getDashboardBundle(venueId) {
    touchVenueDashboardActivity();
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/dashboard-bundle`, {
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      }
    });
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(await this.parseError(response));
    }
    return response.json();
  }

  async getOperatingHours(venueId, spaceId = null) {
    try {
      const query = spaceId ? `?space_id=${encodeURIComponent(spaceId)}` : '';
      const response = await fetch(`${API_BASE_URL}/venues/${venueId}/operating-hours${query}`);
      
      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      
      const data = await response.json();
      return this.transformOperatingHoursList(data);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Update operating hours for a venue
   * @param {string} venueId - Venue ID
   * @param {Object} operatingHours - Operating hours data
   * @returns {Promise<Object>} Updated operating hours
   */
  async updateOperatingHours(venueId, operatingHours, spaceId = null) {
    try {

      // Convert object format (keyed by day number 1-7) to array format expected by API
      const operatingHoursArray = Object.entries(operatingHours).map(([dayNumber, hours]) => {
        // Convert our day numbering (1=Monday, 7=Sunday) to API's (0=Sunday, 1=Monday)
        let apiDayOfWeek;
        if (dayNumber === '7') {
          apiDayOfWeek = 0; // Sunday
        } else {
          apiDayOfWeek = parseInt(dayNumber); // Monday=1, Tuesday=2, etc.
        }
        
        // Validate and clean the data
        const cleanHours = {
          day_of_week: apiDayOfWeek,
          is_open: Boolean(hours.is_open),
          open_time: hours.open_time || '09:00:00',
          close_time: hours.close_time || '17:00:00'
        };
        

        return cleanHours;
      });


      const query = spaceId ? `?space_id=${encodeURIComponent(spaceId)}` : '';
      const body = { operating_hours: operatingHoursArray };
      if (spaceId) body.space_id = spaceId;
      const response = await fetch(`${API_BASE_URL}/venues/${venueId}/operating-hours${query}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      
      const data = await response.json();
      if (Array.isArray(data)) {
        return data;
      }
      if (data && Array.isArray(data.items)) {
        return data.items;
      }
      return [];
    } catch (error) {

      throw error;
    }
  }

  /**
   * Get holidays for a venue
   * @param {string} venueId - Venue ID
   * @returns {Promise<Array>} Holidays data
   */
  async getHolidays(venueId) {
    try {
      // Add timestamp to prevent any caching (browser, CDN, proxy)
      const timestamp = new Date().getTime();
      const url = `${API_BASE_URL}/venues/${venueId}/holidays?_t=${timestamp}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      

      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      
      const data = await response.json();

      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a new holiday for a venue
   * @param {string} venueId - The venue ID
   * @param {Object} holiday - Holiday data
   * @returns {Promise<Object>} Created holiday
   */
  async createHoliday(venueId, holiday) {
    try {
      // Add venue_id to the request body
      const holidayData = {
        venue_id: venueId,
        ...holiday
      };

      const url = `${API_BASE_URL}/venues/${venueId}/holidays`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(holidayData)
      });
      

      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update an existing holiday
   * @param {string} venueId - The venue ID
   * @param {string} holidayId - The holiday ID
   * @param {Object} holiday - Updated holiday data
   * @returns {Promise<Object>} Updated holiday
   */
  async updateHoliday(venueId, holidayId, holiday) {
    try {
      const response = await fetch(`${API_BASE_URL}/venues/${venueId}/holidays/${holidayId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(holiday)
      });
      
      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      
      return await response.json();
    } catch (error) {

      throw error;
    }
  }

  /**
   * Delete a holiday
   * @param {string} venueId - The venue ID
   * @param {string} holidayId - The holiday ID
   * @returns {Promise<void>}
   */
  async deleteHoliday(venueId, holidayId) {
    try {
      const url = `${API_BASE_URL}/venues/${venueId}/holidays/${holidayId}`;

      const response = await fetch(url, {
        method: 'DELETE'
      });
      

      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      
      return;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get dynamic pricing for a venue
   * @param {string} venueId - Venue ID
   * @returns {Promise<Array>} Dynamic pricing data
   */
  async getDynamicPricing(venueId, spaceId = null) {
    try {
      const query = spaceId ? `?space_id=${encodeURIComponent(spaceId)}` : '';
      const response = await fetch(`${API_BASE_URL}/venues/${venueId}/dynamic-pricing${query}`);
      
      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      
      return await response.json();
    } catch (error) {

      throw error;
    }
  }

  /**
   * Create a new dynamic pricing rule
   * @param {string} venueId - The venue ID
   * @param {Object} pricing - Pricing data
   * @returns {Promise<Object>} Created pricing rule
   */
  async createDynamicPricing(venueId, pricing) {
    try {
      // Add venue_id to the request body
      const pricingData = {
        venue_id: venueId,
        ...pricing
      };

      const url = `${API_BASE_URL}/venues/${venueId}/dynamic-pricing`;
      

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pricingData)
      });
      

      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete a dynamic pricing rule
   * @param {string} venueId - The venue ID
   * @param {string} pricingId - The pricing rule ID
   * @returns {Promise<void>}
   */
  async deleteDynamicPricing(venueId, pricingId) {
    try {
      const response = await fetch(`${API_BASE_URL}/venues/${venueId}/dynamic-pricing/${pricingId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      
      return;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Get default per-person price for a venue
   * @param {string} venueId - Venue ID
   * @returns {Promise<Object>} { venue_id, default_per_person_price }
   */
  async getDefaultPerPersonPrice(venueId) {
    try {
      const response = await fetch(`${API_BASE_URL}/venues/${venueId}/default-per-person-price`);
      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update default per-person price for a venue
   * @param {string} venueId - Venue ID
   * @param {number} defaultPerPersonPrice - New default per-person price
   * @returns {Promise<Object>} Updated price info
   */
  async updateDefaultPerPersonPrice(venueId, defaultPerPersonPrice) {
    try {
      const response = await fetch(`${API_BASE_URL}/venues/${venueId}/default-per-person-price`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ default_per_person_price: defaultPerPersonPrice })
      });
      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get dynamic per-person pricing rules for a venue
   * @param {string} venueId - Venue ID
   * @param {string} [spaceId] - Optional space ID filter
   * @returns {Promise<Array>} Dynamic per-person pricing rules
   */
  async getDynamicPricingPerPerson(venueId, spaceId = null) {
    try {
      let url = `${API_BASE_URL}/venues/${venueId}/dynamic-pricing-per-person`;
      if (spaceId) {
        url += `?space_id=${encodeURIComponent(spaceId)}`;
      }
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      const data = await response.json();
      return Array.isArray(data) ? data : (data?.items ?? []);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a dynamic per-person pricing rule
   * @param {string} venueId - Venue ID
   * @param {Object} pricing - { start_date, end_date?, start_time?, end_time?, price_per_person, space_id? }
   * @returns {Promise<Object>} Created rule
   */
  async createDynamicPricingPerPerson(venueId, pricing) {
    try {
      const response = await fetch(`${API_BASE_URL}/venues/${venueId}/dynamic-pricing-per-person`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venue_id: venueId, ...pricing })
      });
      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update a dynamic per-person pricing rule
   * @param {string} venueId - Venue ID
   * @param {string} pricingId - Rule ID
   * @param {Object} updates - Partial updates
   * @returns {Promise<Object>} Updated rule
   */
  async updateDynamicPricingPerPerson(venueId, pricingId, updates) {
    try {
      const response = await fetch(`${API_BASE_URL}/venues/${venueId}/dynamic-pricing-per-person/${pricingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete a dynamic per-person pricing rule
   * @param {string} venueId - Venue ID
   * @param {string} pricingId - Rule ID
   * @returns {Promise<void>}
   */
  async deleteDynamicPricingPerPerson(venueId, pricingId) {
    try {
      const response = await fetch(`${API_BASE_URL}/venues/${venueId}/dynamic-pricing-per-person/${pricingId}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update default price for a venue
   * @param {string} venueId - The venue ID
   * @param {number} hourlyRate - New hourly rate
   * @returns {Promise<Object>} Updated price info
   */
  async updateDefaultPrice(venueId, hourlyRate) {
    try {
      const response = await fetch(`${API_BASE_URL}/venues/${venueId}/default-price`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ default_price: hourlyRate })
      });
      
      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      
      return await response.json();
    } catch (error) {

      throw error;
    }
  }

  /**
   * Get busy times for a venue
   * API: GET https://7s8495kwo0.execute-api.eu-west-2.amazonaws.com/prod/venues/{venueId}/busy-times
   * Query params: start (YYYY-MM-DD), end (YYYY-MM-DD)
   * Response: Array of CalendarBusyTimeResponse { start_time, end_time, space_id, provider, event_title, ... }
   * Times in response are ISO 8601 with timezone (e.g. 2026-03-06T09:00:00+00:00). Display uses UK (Europe/London).
   *
   * @param {string} venueId - The venue ID
   * @param {Date} startDate - Optional start date
   * @param {Date} endDate - Optional end date
   * @param {string|null} spaceId - Optional space filter (multi-space venues)
   * @returns {Promise<Array>} Array of busy times
   */
  async getBusyTimes(venueId, startDate = null, endDate = null, spaceId = null) {
    try {
      const params = new URLSearchParams();
      if (spaceId) {
        params.append('space_id', String(spaceId));
      }
      
      // Helper to format date - try date-only format first (YYYY-MM-DD)
      // If that doesn't work, API might want full ISO-8601 datetime
      const formatDateForAPI = (date) => {
        if (!date) return null;
        // Extract date components using UTC to avoid timezone issues
        const dateObj = date instanceof Date ? date : new Date(date);
        const year = dateObj.getUTCFullYear();
        const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getUTCDate()).padStart(2, '0');
        // Return date-only format (YYYY-MM-DD) - ISO-8601 date format
        return `${year}-${month}-${day}`;
      };
      
      if (startDate) {
        params.append('start', formatDateForAPI(startDate));
      }
      if (endDate) {
        params.append('end', formatDateForAPI(endDate));
      }
      
      // Default to next 30 days if no dates provided
      if (!startDate && !endDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const nextMonth = new Date(today);
        nextMonth.setDate(nextMonth.getDate() + 30);
        params.append('start', formatDateForAPI(today));
        params.append('end', formatDateForAPI(nextMonth));
      }
      
      const queryString = params.toString();
      const url = `${API_BASE_URL}/venues/${venueId}/busy-times${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        // Don't throw for 404 - just return empty array (venue might not have busy times)
        if (response.status === 404) {
          return [];
        }
        throw new Error(await this.parseError(response));
      }
      
      const data = await response.json();
      
      // The API returns List[CalendarBusyTimeResponse] directly
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Get venue details
   * @param {string} venueId - The venue ID
   * @returns {Promise<Object>} Venue details
   */
  async getVenueDetails(venueId, { forceRefresh = false } = {}) {
    touchVenueDashboardActivity();
    try {
      return await fetchVenueRecordCached(
        venueId,
        async (id) => {
          const response = await fetch(`${API_BASE_URL}/venues/${id}`);

          if (!response.ok) {
            throw new Error(await this.parseError(response));
          }

          return await response.json();
        },
        { forceRefresh }
      );
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user by ID (e.g. venue owner)
   * API: GET /users/{user_id}
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} User profile with first_name, last_name, email, phone, etc.
   */
  async getUserById(userId) {
    if (!userId) return null;
    try {
      const url = `${API_BASE_URL}/users/${userId}`;
      const response = await fetch(url, {
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() }
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (e) {
      return null;
    }
  }

  /**
   * Unified availability API
   * GET /venues/{venue_id}/spaces/{space_id}/availability
   * mode=blocks – Returns busy blocks (calendar + manual + capacity-exhausted)
   * mode=preview – Returns slot availability, blocking reasons, and pricing
   *
   * @param {string} venueId - Venue ID
   * @param {string} spaceId - Space ID
   * @param {Object} params - mode, date, start_time, end_time, booking_mode, headcount
   * @returns {Promise<Object|Array>} Response (blocks array or preview object)
   */
  async getSpaceAvailability(venueId, spaceId, params) {
    if (!venueId || !spaceId || spaceId.includes('{')) return null;
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') {
        const key = k.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
        searchParams.set(key, String(v));
      }
    });
    const url = `${API_BASE_URL}/venues/${venueId}/spaces/${spaceId}/availability?${searchParams.toString()}`;
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() }
    });
    if (!response.ok) {
      if (response.status === 404) return null;
      const err = await this.parseError(response);
      throw new Error(err);
    }
    const data = await response.json();
    return data;
  }

  /**
   * Get flat pricing rules for a venue
   * @param {string} venueId - Venue ID
   * @param {string} [spaceId] - Optional space ID to filter
   * @returns {Promise<Array>} Flat pricing rules
   */
  async getFlatPricing(venueId, spaceId = null) {
    const url = spaceId
      ? `${API_BASE_URL}/venues/${venueId}/flat-pricing?space_id=${spaceId}`
      : `${API_BASE_URL}/venues/${venueId}/flat-pricing`;
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() }
    });
    if (!response.ok) {
      if (response.status === 404) return [];
      throw new Error(await this.parseError(response));
    }
    const data = await response.json();
    const items = Array.isArray(data)
      ? data
      : (data?.items ?? data?.flat_pricing ?? data?.data);
    const arr = Array.isArray(items) ? items : [];
    return arr;
  }

  async createFlatPricing(venueId, rule) {
    const url = `${API_BASE_URL}/venues/${venueId}/flat-pricing`;
    const body = {
      min_hours: Number(rule.min_hours),
      flat_price: Number(rule.flat_price),
      is_active: rule.is_active !== false,
      ...(rule.space_id && { space_id: rule.space_id })
    };
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const errMsg = await this.parseError(response);
      throw new Error(errMsg);
    }
    const data = await response.json();
    return data;
  }

  async updateFlatPricing(venueId, pricingId, updates) {
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/flat-pricing/${pricingId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify(updates)
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  async deleteFlatPricing(venueId, pricingId) {
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/flat-pricing/${pricingId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) throw new Error(await this.parseError(response));
  }

  async getBookingRules(venueId) {
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/booking-rules`, {
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() }
    });
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(await this.parseError(response));
    }
    return response.json();
  }

  async updateBookingRules(venueId, rules) {
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/booking-rules`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify(rules)
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  async getOperatingHoursPerPerson(venueId, spaceId = null) {
    const query = spaceId ? `?space_id=${encodeURIComponent(spaceId)}` : '';
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/operating-hours-per-person${query}`, {
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() }
    });
    if (!response.ok) {
      if (response.status === 404) return {};
      throw new Error(await this.parseError(response));
    }
    const data = await response.json();
    return this.transformOperatingHoursList(data);
  }

  async updateOperatingHoursPerPerson(venueId, operatingHours, spaceId = null) {
    const operatingHoursArray = Object.entries(operatingHours).map(([dayNumber, hours]) => {
      const apiDayOfWeek = dayNumber === '7' ? 0 : parseInt(dayNumber, 10);
      return {
        day_of_week: apiDayOfWeek,
        is_open: Boolean(hours.is_open),
        open_time: hours.open_time || '09:00:00',
        close_time: hours.close_time || '17:00:00'
      };
    });
    const query = spaceId ? `?space_id=${encodeURIComponent(spaceId)}` : '';
    const body = { operating_hours: operatingHoursArray };
    if (spaceId) body.space_id = spaceId;
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/operating-hours-per-person${query}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /**
   * Get busy blocks for a space/date (unified API mode=blocks).
   * Returns calendar + manual + capacity-exhausted blocks.
   *
   * @param {string} venueId - Venue ID
   * @param {string} spaceId - Space ID
   * @param {string} date - YYYY-MM-DD
   * @returns {Promise<Array>} Array of blocks { start_time, end_time, provider, event_title, ... }
   */
  /**
   * @param {string} venueId - Venue ID
   * @param {string} spaceId - Space ID
   * @param {string} date - YYYY-MM-DD
   * @param {string} [bookingMode] - 'exclusive' (full-venue) or 'per_person'. Defaults to exclusive.
   */
  async getSpaceAvailabilityBlocks(venueId, spaceId, date, bookingMode = 'exclusive', { forceRefresh = false } = {}) {
    if (!venueId || !spaceId || !date || spaceId.includes('{')) return [];
    if (!forceRefresh) {
      const cached = getCachedAvailabilityBlocks(venueId, spaceId, date, bookingMode);
      if (cached) return cached;
    }
    try {
      const params = { mode: 'blocks', date, booking_mode: bookingMode };
      const data = await this.getSpaceAvailability(venueId, spaceId, params);
      const blocks = Array.isArray(data) ? data : (data?.blocks ?? []);
      setCachedAvailabilityBlocks(venueId, spaceId, date, bookingMode, blocks);
      return blocks;
    } catch {
      return [];
    }
  }

  /**
   * Send "venue approved your booking, pay now" email to the event organizer.
   * Call when the venue owner approves a pending booking.
   * @param {string} bookingId - Booking ID
   * @param {Object} [params] - Optional query params
   * @param {string} [params.payment_deadline] - Override deadline (e.g. '2026-03-12 18:00')
   * @param {string} [params.payment_link] - Override payment URL
   * @param {'invoice'|'occasia_stripe'|'external_link'|'free'} [params.payment_mode] - How organiser pays
   * @returns {Promise<Object>} { success, message, booking_id }
   */
  async sendBookingApprovedEmail(bookingId, params = {}) {
    const url = `${API_BASE_URL}/bookings/${bookingId}/send-booking-approved-email`;
    const body = {};
    if (params.payment_deadline) body.payment_deadline = params.payment_deadline;
    if (params.payment_link) body.payment_link = params.payment_link;
    if (params.payment_mode) body.payment_mode = params.payment_mode;
    if (params.is_payment_reminder) body.is_payment_reminder = true;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /**
   * Notify organiser that an invoice was uploaded and payment is due.
   * Uses payment-reminder endpoint until a dedicated invoice template exists on the API.
   */
  async sendInvoiceUploadedEmail(bookingId, params = {}) {
    const searchParams = new URLSearchParams();
    if (params.payment_link) searchParams.set('payment_link', params.payment_link);
    if (params.payment_mode) searchParams.set('payment_mode', params.payment_mode);
    const query = searchParams.toString();
    const url = `${API_BASE_URL}/bookings/${bookingId}/send-invoice-uploaded-email${query ? `?${query}` : ''}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify({
        payment_link: params.payment_link,
        payment_mode: params.payment_mode
      })
    });
    if (response.status === 404) {
      return this.sendPaymentReminder(bookingId, params);
    }
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /**
   * @param {string} bookingId - Booking ID
   * @param {Object} [params] - Optional query params
   * @param {string} [params.payment_deadline] - Override deadline (e.g. '2026-03-17 18:00')
   * @param {string} [params.payment_link] - Override payment URL
   * @param {'invoice'|'occasia_stripe'|'external_link'|'free'} [params.payment_mode] - How organiser pays
   * @returns {Promise<Object>} { success, message, booking_id }
   */
  async sendPaymentReminder(bookingId, params = {}) {
    const url = `${API_BASE_URL}/bookings/${bookingId}/send-payment-reminder`;
    const body = {};
    if (params.payment_deadline) body.payment_deadline = params.payment_deadline;
    if (params.payment_link) body.payment_link = params.payment_link;
    if (params.payment_mode) body.payment_mode = params.payment_mode;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /**
   * Post-event feedback request email to the organizer.
   * @param {string} bookingId
   * @returns {Promise<Object>}
   */
  async sendFeedbackRequest(bookingId) {
    const url = `${API_BASE_URL}/bookings/${bookingId}/send-feedback-request`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /**
   * Feedback submission status for a booking.
   * @param {string} bookingId
   * @returns {Promise<Object>}
   */
  async getBookingFeedback(bookingId) {
    const url = `${API_BASE_URL}/bookings/${bookingId}/feedback`;
    const response = await fetch(url, {
      headers: { ...this.getAuthHeaders() },
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /**
   * Bookings for a venue (same fallbacks as useVenueBookings).
   * @param {string} venueId
   * @returns {Promise<Array>}
   */
  async fetchVenueBookingsList(venueId) {
    if (!venueId) return [];
    const headers = { ...this.getAuthHeaders(), 'Content-Type': 'application/json' };
    const data = await this.fetchWithFallback(
      [
        `${API_BASE_URL}/bookings?venue_id=${encodeURIComponent(String(venueId))}`,
        `${API_BASE_URL}/bookings/venue/${encodeURIComponent(String(venueId))}`,
        `${API_BASE_URL}/venue-owner/bookings`,
      ],
      { headers },
      { allowEmpty: true }
    );
    const bookingsArray = Array.isArray(data)
      ? data
      : (data?.bookings && Array.isArray(data.bookings) ? data.bookings : []);
    return bookingsArray.filter((b) => b && String(b.venue_id) === String(venueId));
  }

  /**
   * Build venue feedback list from per-booking feedback endpoints (pre-list-API fallback).
   * @param {Array} bookings
   * @param {string} venueId
   * @returns {Promise<Object>}
   */
  async aggregateVenueFeedbackFromBookings(bookings, venueId) {
    const scoped = (bookings || []).filter(
      (b) => b && String(b.venue_id || venueId) === String(venueId)
    );
    const items = [];
    const batchSize = 8;
    for (let i = 0; i < scoped.length; i += batchSize) {
      const batch = scoped.slice(i, i + batchSize);
      const results = await Promise.allSettled(
        batch.map((booking) => {
          const bookingId = booking.id ?? booking.booking_id;
          if (!bookingId) return Promise.resolve(null);
          return this.getBookingFeedback(bookingId);
        })
      );
      results.forEach((result, index) => {
        if (result.status !== 'fulfilled' || !result.value) return;
        const fb = result.value.feedback;
        if (!fb?.rating) return;
        const booking = batch[index];
        items.push({
          id: fb.id,
          booking_id: String(booking.id ?? booking.booking_id),
          venue_id: String(venueId),
          organizer_email: booking.customer_email || booking.contact_email || null,
          rating: fb.rating,
          comment: fb.comment,
          submitted_at: fb.submitted_at,
          source: fb.source,
          booking_reference: booking.booking_reference,
          booking_date: booking.booking_date,
          start_time: booking.start_time,
          end_time: booking.end_time,
          event_name: booking.event_name,
          booking_status: booking.status,
          payment_status: booking.payment_status,
          feedback_request_sent_count: result.value.feedback_request_sent_count ?? 0,
        });
      });
    }
    items.sort((a, b) => {
      const ta = a.submitted_at ? new Date(a.submitted_at).getTime() : 0;
      const tb = b.submitted_at ? new Date(b.submitted_at).getTime() : 0;
      return tb - ta;
    });
    const ratings = items.map((row) => Number(row.rating)).filter((n) => !Number.isNaN(n));
    const average_rating = ratings.length
      ? Math.round((ratings.reduce((sum, n) => sum + n, 0) / ratings.length) * 10) / 10
      : null;
    return {
      venue_id: String(venueId),
      count: items.length,
      average_rating,
      feedback: items,
    };
  }

  /**
   * All submitted customer feedback for a venue (venue dashboard tab).
   * Falls back to per-booking aggregation when the list endpoint is not deployed yet.
   * @param {string} venueId
   * @param {{ bookings?: Array }} [options]
   * @returns {Promise<Object>}
   */
  async getVenueFeedback(venueId, { bookings = null } = {}) {
    const url = `${API_BASE_URL}/venues/${venueId}/feedback`;
    const response = await fetch(url, {
      headers: { ...this.getAuthHeaders() },
    });
    if (response.ok) {
      return response.json();
    }
    if (response.status === 404) {
      const bookingList = bookings ?? (await this.fetchVenueBookingsList(venueId));
      return this.aggregateVenueFeedbackFromBookings(bookingList, venueId);
    }
    throw new Error(await this.parseError(response));
  }

  /**
   * Send booking update confirmation emails to the organiser and venue owner.
   * Call after the venue owner saves edited booking details.
   * @param {string} bookingId - Booking ID
   * @returns {Promise<Object>} { success, message, booking_id }
   */
  async sendBookingUpdatedEmail(bookingId) {
    const url = `${API_BASE_URL}/bookings/${bookingId}/send-booking-updated-email`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() }
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /**
   * Notify organiser (and venue) that a booking was cancelled.
   * @param {string} bookingId
   * @param {Object} [params]
   * @param {string} [params.message] - Optional message to include in the email
   * @param {string} [params.reason] - Cancellation reason
   * @param {'owner'|'user'} [params.cancelled_by]
   */
  async sendBookingCancelledEmail(bookingId, params = {}) {
    const url = `${API_BASE_URL}/bookings/${bookingId}/send-booking-cancelled-email`;
    const body = {};
    if (params.message) body.message = params.message;
    if (params.reason) body.reason = params.reason;
    if (params.cancelled_by) body.cancelled_by = params.cancelled_by;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    const text = await response.text();
    if (!text) return { success: true };
    try {
      return JSON.parse(text);
    } catch {
      return { success: true };
    }
  }

  /**
   * Cancel a booking (venue owner or organiser). May also trigger notification emails on the API.
   */
  async cancelBooking(bookingId, { reason = '', message = '', cancelledBy = 'owner' } = {}) {
    if (!bookingId) throw new Error('bookingId is required');
    const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}/cancel`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify({
        reason: reason || message || 'Cancelled by venue',
        message,
        cancelled_by: cancelledBy,
        cancelled_at: new Date().toISOString()
      })
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    const text = await response.text();
    if (!text) return { success: true };
    try {
      return JSON.parse(text);
    } catch {
      return { success: true };
    }
  }

  /**
   * Public catalog of add-on/package filter tags.
   * @param {{ includeInactive?: boolean }} [options]
   * @returns {Promise<Array>}
   */
  async getCatalogAddonTags({ includeInactive = false } = {}) {
    const params = includeInactive ? '?include_inactive=true' : '';
    const response = await fetch(`${API_BASE_URL}/catalog-addon-tags${params}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  /** Admin: list all catalog tags. */
  async getAdminCatalogAddonTags() {
    const response = await fetch(`${API_BASE_URL}/admin/catalog-addon-tags`, {
      headers: { ...this.getAuthHeaders() },
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  /** Admin: create catalog tag. */
  async createCatalogAddonTag(payload) {
    const response = await fetch(`${API_BASE_URL}/admin/catalog-addon-tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /** Admin: update catalog tag. */
  async updateCatalogAddonTag(tagId, payload) {
    const response = await fetch(`${API_BASE_URL}/admin/catalog-addon-tags/${tagId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /** Admin: delete catalog tag. */
  async deleteCatalogAddonTag(tagId) {
    const response = await fetch(`${API_BASE_URL}/admin/catalog-addon-tags/${tagId}`, {
      method: 'DELETE',
      headers: { ...this.getAuthHeaders() },
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /**
   * Fetch a single booking by id.
   * @param {string} bookingId
   * @returns {Promise<Object>}
   */
  async getBookingById(bookingId) {
    if (!bookingId) throw new Error('bookingId is required');
    const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}`, {
      headers: { ...this.getAuthHeaders() },
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /**
   * Delete a booking (used to roll back manual create when invoice upload fails).
   */
  async deleteBooking(bookingId) {
    if (!bookingId) throw new Error('bookingId is required');
    const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}`, {
      method: 'DELETE',
      headers: { ...this.getAuthHeaders() }
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    const text = await response.text();
    if (!text) return { success: true };
    try {
      return JSON.parse(text);
    } catch {
      return { success: true };
    }
  }

  /**
   * Partial update for a booking (e.g. add-ons: packages, add_ons_total).
   */
  async updateBooking(bookingId, payload) {
    if (!bookingId) throw new Error('bookingId is required');
    const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch {
      return {};
    }
  }

  /** CDC audit trail for who changed a booking and which API call was made. */
  async getBookingAuditLog(bookingId, { limit = 100 } = {}) {
    if (!bookingId) throw new Error('bookingId is required');
    const params = new URLSearchParams();
    if (limit) params.set('limit', String(limit));
    const query = params.toString();
    const response = await fetch(
      `${API_BASE_URL}/bookings/${bookingId}/audit-log${query ? `?${query}` : ''}`,
      {
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() }
      }
    );
    if (!response.ok) throw new Error(await this.parseError(response));
    const data = await response.json();
    return Array.isArray(data?.entries) ? data.entries : [];
  }

  /**
   * Resolve actor value for owner_id/user_id query params.
   * API accepts UUID or email.
   */
  resolveInvoiceActor(actor) {
    if (!actor) return null;
    if (typeof actor === 'string') return actor;
    return actor.id || actor.user_id || actor.email || null;
  }

  async listBookingOwnerInvoices(bookingId, { ownerId, userId } = {}) {
    if (!bookingId) return [];
    const params = new URLSearchParams();
    const resolvedOwner = this.resolveInvoiceActor(ownerId);
    const resolvedUser = this.resolveInvoiceActor(userId);
    if (resolvedOwner) params.set('owner_id', resolvedOwner);
    if (resolvedUser) params.set('user_id', resolvedUser);

    const response = await fetch(
      `${API_BASE_URL}/bookings/${bookingId}/owner-invoices${params.toString() ? `?${params.toString()}` : ''}`,
      {
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() }
      }
    );
    if (!response.ok) {
      if (response.status === 404) return [];
      throw new Error(await this.parseError(response));
    }
    const data = await response.json();
    return Array.isArray(data) ? data : (data?.items ?? data?.files ?? []);
  }

  /** List static booking notes (enquiry / manual booking text). Not conversation messages. */
  async listBookingNotes(bookingId, { ownerId, userId } = {}) {
    if (!bookingId) return [];
    const params = new URLSearchParams();
    const resolvedOwner = this.resolveInvoiceActor(ownerId);
    const resolvedUser = this.resolveInvoiceActor(userId);
    if (resolvedOwner) params.set('owner_id', resolvedOwner);
    if (resolvedUser) params.set('user_id', resolvedUser);

    const response = await fetch(
      `${API_BASE_URL}/bookings/${bookingId}/notes${params.toString() ? `?${params.toString()}` : ''}`,
      {
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() }
      }
    );
    if (!response.ok) {
      if (response.status === 404) return [];
      throw new Error(await this.parseError(response));
    }
    const data = await response.json();
    return Array.isArray(data) ? data : (data?.items ?? data?.notes ?? []);
  }

  async createBookingNote(bookingId, body, { ownerId, userId, source = 'venue_internal' } = {}) {
    if (!bookingId) throw new Error('bookingId is required');
    const text = (body || '').trim();
    if (!text) throw new Error('Note text is required');

    const params = new URLSearchParams();
    const resolvedOwner = this.resolveInvoiceActor(ownerId);
    const resolvedUser = this.resolveInvoiceActor(userId);
    if (resolvedOwner) params.set('owner_id', resolvedOwner);
    if (resolvedUser) params.set('user_id', resolvedUser);

    const response = await fetch(
      `${API_BASE_URL}/bookings/${bookingId}/notes${params.toString() ? `?${params.toString()}` : ''}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
        body: JSON.stringify({ body: text, source })
      }
    );
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  async updateBookingNote(bookingId, noteId, body, { ownerId, userId } = {}) {
    if (!bookingId || !noteId) throw new Error('bookingId and noteId are required');
    const text = (body || '').trim();
    if (!text) throw new Error('Note text is required');

    const params = new URLSearchParams();
    const resolvedOwner = this.resolveInvoiceActor(ownerId);
    const resolvedUser = this.resolveInvoiceActor(userId);
    if (resolvedOwner) params.set('owner_id', resolvedOwner);
    if (resolvedUser) params.set('user_id', resolvedUser);

    const response = await fetch(
      `${API_BASE_URL}/bookings/${bookingId}/notes/${noteId}?${params.toString()}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
        body: JSON.stringify({ body: text })
      }
    );
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /**
   * Get a presigned invoice download URL.
   */
  async getBookingOwnerInvoiceDownloadUrl(bookingId, fileId, { ownerId, userId } = {}) {
    if (!bookingId || !fileId) throw new Error('bookingId and fileId are required');
    const params = new URLSearchParams();
    const resolvedOwner = this.resolveInvoiceActor(ownerId);
    const resolvedUser = this.resolveInvoiceActor(userId);
    if (resolvedOwner) params.set('owner_id', resolvedOwner);
    if (resolvedUser) params.set('user_id', resolvedUser);

    const response = await fetch(
      `${API_BASE_URL}/bookings/${bookingId}/owner-invoices/${fileId}/download-url?${params.toString()}`,
      {
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() }
      }
    );
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /**
   * Create a presigned upload URL for booking owner invoices.
   */
  async createBookingOwnerInvoiceUploadUrl(bookingId, { ownerId, filename, contentType }) {
    if (!bookingId || !ownerId || !filename || !contentType) {
      throw new Error('bookingId, ownerId, filename and contentType are required');
    }
    const resolvedOwner = this.resolveInvoiceActor(ownerId);
    const response = await fetch(
      `${API_BASE_URL}/bookings/${bookingId}/owner-invoices/upload-url?owner_id=${encodeURIComponent(resolvedOwner)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
        body: JSON.stringify({
          filename,
          content_type: contentType
        })
      }
    );
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /**
   * Complete invoice upload and mark file as ready.
   */
  async completeBookingOwnerInvoiceUpload(bookingId, fileId, { ownerId }) {
    if (!bookingId || !fileId || !ownerId) {
      throw new Error('bookingId, fileId and ownerId are required');
    }
    const resolvedOwner = this.resolveInvoiceActor(ownerId);
    const response = await fetch(
      `${API_BASE_URL}/bookings/${bookingId}/owner-invoices/${fileId}/complete?owner_id=${encodeURIComponent(resolvedOwner)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() }
      }
    );
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /**
   * Delete owner invoice file.
   */
  async deleteBookingOwnerInvoice(bookingId, fileId, { ownerId }) {
    if (!bookingId || !fileId || !ownerId) {
      throw new Error('bookingId, fileId and ownerId are required');
    }
    const resolvedOwner = this.resolveInvoiceActor(ownerId);
    const response = await fetch(
      `${API_BASE_URL}/bookings/${bookingId}/owner-invoices/${fileId}?owner_id=${encodeURIComponent(resolvedOwner)}`,
      {
        method: 'DELETE',
        headers: { ...this.getAuthHeaders() }
      }
    );
    if (!response.ok) throw new Error(await this.parseError(response));
  }

  /**
   * Upload invoice via API proxy (server writes to S3). Avoids browser direct-to-S3 failures.
   */
  async uploadBookingOwnerInvoiceDirect(bookingId, file, { ownerId } = {}) {
    if (!bookingId || !file || !ownerId) {
      throw new Error('bookingId, file and ownerId are required');
    }
    const contentType = resolveInvoiceFileContentType(file);
    if (!contentType) {
      throw new Error('Only PDF, DOC, and DOCX files are supported');
    }

    const resolvedOwner = this.resolveInvoiceActor(ownerId);
    const formData = new FormData();
    formData.append('file', file, file.name || 'invoice.pdf');

    const response = await fetch(
      `${API_BASE_URL}/bookings/${bookingId}/owner-invoices/upload?owner_id=${encodeURIComponent(resolvedOwner)}`,
      {
        method: 'POST',
        headers: { ...this.getAuthHeaders() },
        body: formData
      }
    );
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /**
   * Upload (or replace) an owner invoice file end-to-end.
   */
  async uploadBookingOwnerInvoice(bookingId, file, { ownerId, replaceFileId } = {}) {
    if (!file) throw new Error('Invoice file is required');
    const contentType = resolveInvoiceFileContentType(file);
    if (!contentType) {
      throw new Error('Only PDF, DOC, and DOCX files are supported');
    }

    if (replaceFileId) {
      await this.deleteBookingOwnerInvoice(bookingId, replaceFileId, { ownerId });
    }

    try {
      return await this.uploadBookingOwnerInvoiceDirect(bookingId, file, { ownerId });
    } catch (directErr) {
      if (!shouldFallbackToPresignedInvoiceUpload(directErr)) {
        throw directErr;
      }
    }

    const uploadInfo = await this.createBookingOwnerInvoiceUploadUrl(bookingId, {
      ownerId,
      filename: file.name,
      contentType
    });

    await putInvoiceFileToPresignedUrl(uploadInfo.upload_url, file);

    return this.completeBookingOwnerInvoiceUpload(bookingId, uploadInfo.file_id, { ownerId });
  }

  /**
   * Venue gallery images (authenticated venue owner JWT).
   * Flow: createVenueImageUploadUrl → PUT to upload_url → completeVenueImageUpload.
   */
  async createVenueImageUploadUrl(venueId, { filename, contentType, replaceAll = false } = {}) {
    if (!venueId || !filename || !contentType) {
      throw new Error('venueId, filename and contentType are required');
    }
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/images/upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify({
        filename,
        content_type: contentType,
        replace_all: Boolean(replaceAll)
      })
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  /**
   * PUT file bytes to presigned upload_url.
   */
  async uploadVenueImageFile(uploadUrl, file, suggestedHeaders = {}) {
    const ct =
      (suggestedHeaders && (suggestedHeaders['Content-Type'] || suggestedHeaders['content-type'])) ||
      file?.type ||
      '';
    const headers = {};
    if (ct) headers['Content-Type'] = ct;
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers,
      body: file
    });
    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload venue image (${uploadResponse.status})`);
    }
    return uploadResponse;
  }

  /**
   * Upload a per-venue host photo (gallery presign → PUT → complete).
   * The file also appears in the gallery API; we hide host-photo.* entries in the UI
   * and in public venue transforms — do not DELETE gallery rows here, that removes the CDN file.
   */
  async uploadHostPhoto(venueId, file) {
    if (!venueId || !file) {
      throw new Error('venueId and file are required');
    }
    const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
    if (!allowedTypes.has(file.type)) {
      throw new Error('Host photo must be JPG, PNG, or WebP.');
    }
    const ext =
      file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
    const filename = `host-photo.${ext}`;
    const uploadInfo = await this.createVenueImageUploadUrl(venueId, {
      filename,
      contentType: file.type,
      replaceAll: false
    });
    await this.uploadVenueImageFile(
      uploadInfo.upload_url,
      file,
      uploadInfo.suggested_headers || uploadInfo.required_headers || {}
    );
    const imageId = uploadInfo.image_id || uploadInfo.id;
    if (!imageId) {
      throw new Error('Host photo upload could not be finalized (missing image id).');
    }
    const completed = await this.completeVenueImageUpload(venueId, imageId);
    const imageUrl =
      completed?.image_url ||
      completed?.url ||
      uploadInfo.image_url ||
      uploadInfo.public_url ||
      null;
    if (!imageUrl) {
      throw new Error('Host photo upload completed but no image URL was returned.');
    }
    const storedFilename = hostPhotoFilenameFromStoredUrl(imageUrl, venueId) || filename;
    return { filename: storedFilename, imageUrl, imageId };
  }

  async completeVenueImageUpload(venueId, imageId) {
    if (!venueId || !imageId) throw new Error('venueId and imageId are required');
    const response = await fetch(
      `${API_BASE_URL}/venues/${venueId}/images/${encodeURIComponent(imageId)}/complete`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() }
      }
    );
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  async listVenueImages(venueId) {
    if (!venueId) return [];
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/images`, {
      headers: { ...this.getAuthHeaders(), Accept: 'application/json' }
    });
    if (!response.ok) {
      if (response.status === 404) return [];
      throw new Error(await this.parseError(response));
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  }

  async patchVenueImage(venueId, imageId, { caption, isPrimary } = {}) {
    if (!venueId || !imageId) throw new Error('venueId and imageId are required');
    const body = {};
    if (caption !== undefined) body.caption = caption;
    if (isPrimary !== undefined) body.is_primary = isPrimary;
    if (Object.keys(body).length === 0) throw new Error('Nothing to patch');
    const response = await fetch(
      `${API_BASE_URL}/venues/${venueId}/images/${encodeURIComponent(imageId)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
        body: JSON.stringify(body)
      }
    );
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  async setPrimaryVenueImage(venueId, imageId, { caption } = {}) {
    return this.patchVenueImage(venueId, imageId, {
      ...(caption !== undefined ? { caption } : {}),
      isPrimary: true
    });
  }

  async deleteVenueImage(venueId, imageId) {
    if (!venueId || !imageId) throw new Error('venueId and imageId are required');
    const response = await fetch(
      `${API_BASE_URL}/venues/${venueId}/images/${encodeURIComponent(imageId)}`,
      {
        method: 'DELETE',
        headers: { ...this.getAuthHeaders() }
      }
    );
    if (!response.ok) throw new Error(await this.parseError(response));
    const text = await response.text();
    if (!text) return { deleted: true };
    try {
      return JSON.parse(text);
    } catch {
      return { deleted: true };
    }
  }

  async deleteAllVenueImages(venueId) {
    if (!venueId) throw new Error('venueId is required');
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/images`, {
      method: 'DELETE',
      headers: { ...this.getAuthHeaders() }
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json().catch(() => ({ deleted: true }));
  }

  /**
   * Get space availability and pricing preview (unified API mode=preview).
   * Legacy availability-preview delegates to unified endpoint.
   *
   * @param {string} venueId - The venue ID
   * @param {string} spaceId - The space ID
   * @param {Object} params - Query params
   * @param {string} params.date - Date (YYYY-MM-DD)
   * @param {string} params.startTime - Start time (HH:MM or HH:MM:SS)
   * @param {string} params.endTime - End time (HH:MM or HH:MM:SS)
   * @param {string} [params.bookingMode] - 'exclusive' (full venue) or 'per_person'
   * @param {number} [params.headcount] - Required when bookingMode=per_person
   * @returns {Promise<Object>} { is_available, blocking_reasons, hourly_rate, per_person_rate, estimated_total_amount, duration_hours, capacity_total, capacity_used, remaining_capacity }
   */
  async getSpaceAvailabilityPreview(venueId, spaceId, { date, startTime, endTime, bookingMode = 'exclusive', headcount, timezone }, { forceRefresh = false } = {}) {
    if (!forceRefresh) {
      const cached = getCachedAvailabilityPreview(venueId, spaceId, {
        date,
        start_time: startTime,
        end_time: endTime,
        booking_mode: bookingMode,
        headcount,
        timezone
      });
      if (cached) return cached;
    }
    try {
      const params = {
        mode: 'preview',
        date,
        start_time: startTime?.length >= 5 ? startTime : `${startTime || '00:00'}:00`,
        end_time: endTime?.length >= 5 ? endTime : `${endTime || '00:00'}:00`,
        booking_mode: bookingMode
      };
      if (bookingMode === 'per_person' && headcount != null) {
        params.headcount = headcount;
      }
      if (timezone) {
        params.timezone = timezone;
      }
      const data = await this.getSpaceAvailability(venueId, spaceId, params);
      if (data) {
        setCachedAvailabilityPreview(venueId, spaceId, {
          date,
          start_time: startTime,
          end_time: endTime,
          booking_mode: bookingMode,
          headcount,
          timezone
        }, data);
      }
      return data;
    } catch {
      return null;
    }
  }

  /**
   * Get spaces for a venue
   * @param {string} venueId - The venue ID
   * @returns {Promise<Array>} Venue spaces
   */
  async getVenueSpaces(venueId) {
    try {
      const response = await fetch(`${API_BASE_URL}/venues/${venueId}/spaces`, {
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        }
      });
      if (!response.ok) {
        if (response.status === 404) {
          return [];
        }
        const errorMessage = await this.parseError(response);
        throw new Error(errorMessage);
      }
      const data = await response.json();
      return catalogArrayFromPayload(data, ['spaces', 'data', 'items', 'results']);
    } catch (error) {
      return [];
    }
  }

  /**
   * Update a venue space (e.g. capacity_total for per-person bookings).
   * PUT /venues/{venue_id}/spaces/{space_id}
   */
  async updateVenueSpace(venueId, spaceId, spaceData) {
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/spaces/${spaceId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify(spaceData)
    });
    if (!response.ok) {
      const errorMessage = await this.parseError(response);
      throw new Error(errorMessage);
    }
    return await response.json();
  }

  /**
   * Update venue details
   * PUT /venues/{venue_id} - see VENUES_API.md
   * @param {string} venueId - The venue ID
   * @param {Object} venueData - Updated venue data (only include fields to change)
   * @returns {Promise<Object>} Updated venue (same shape as GET /venues/{id})
   */
  async updateVenueDetails(venueId, venueData) {
    const url = `${API_BASE_URL}/venues/${venueId}`;
    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders()
        },
        body: JSON.stringify(venueData)
      });
      
      if (!response.ok) {
        throw new Error(await this.parseError(response));
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get add-on items for a venue
   * @param {string} venueId - The venue ID
   * @returns {Promise<Array>} List of add-on items
   */
  async getVenueAddOnItems(venueId, { itemKind, spaceId } = {}) {
    const params = new URLSearchParams();
    if (itemKind) params.set('item_kind', itemKind);
    if (spaceId) params.set('space_id', String(spaceId));
    const query = params.toString();
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/items${query ? `?${query}` : ''}`, {
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      }
    });
    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      const errorMessage = await this.parseError(response);
      throw new Error(errorMessage);
    }
    const data = await response.json();
    return catalogArrayFromPayload(data, ['items', 'add_on_items', 'addons']);
  }

  /**
   * Create an add-on item for a venue
   * @param {string} venueId - The venue ID
   * @param {Object} itemData - Item payload
   * @returns {Promise<Object>} Created item
   */
  async createVenueAddOnItem(venueId, itemData) {
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/items`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify(itemData)
    });
    if (!response.ok) {
      const errorMessage = await this.parseError(response);
      throw new Error(errorMessage);
    }
    return await response.json();
  }

  /**
   * Update an add-on item
   * @param {string} venueId - The venue ID
   * @param {string} itemId - Item ID
   * @param {Object} itemData - Updated item payload
   * @returns {Promise<Object>} Updated item
   */
  async updateVenueAddOnItem(venueId, itemId, itemData) {
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/items/${itemId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify(itemData)
    });
    if (!response.ok) {
      const errorMessage = await this.parseError(response);
      throw new Error(errorMessage);
    }
    return await response.json();
  }

  /**
   * Delete an add-on item
   * @param {string} venueId - The venue ID
   * @param {string} itemId - Item ID
   * @returns {Promise<void>}
   */
  async deleteVenueAddOnItem(venueId, itemId) {
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/items/${itemId}`, {
      method: 'DELETE',
      headers: {
        ...this.getAuthHeaders()
      }
    });
    if (!response.ok) {
      const errorMessage = await this.parseError(response);
      throw new Error(errorMessage);
    }
  }

  /**
   * Get packages for a venue
   * @param {string} venueId - The venue ID
   * @returns {Promise<Array>} List of packages
   */
  async getVenuePackages(venueId, spaceId = null) {
    const query = spaceId ? `?space_id=${encodeURIComponent(spaceId)}` : '';
    const url = `${API_BASE_URL}/venues/${venueId}/packages${query}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      }
    });
    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      const errorMessage = await this.parseError(response);
      throw new Error(errorMessage);
    }
    const data = await response.json();
    return catalogArrayFromPayload(data, ['packages', 'items']);
  }

  /**
   * Create a package for a venue
   * @param {string} venueId - The venue ID
   * @param {Object} packageData - Package payload
   * @returns {Promise<Object>} Created package
   */
  async createVenuePackage(venueId, packageData) {
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/packages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify(packageData)
    });
    if (!response.ok) {
      const errorMessage = await this.parseError(response);
      throw new Error(errorMessage);
    }
    return this.parseJsonBody(response);
  }

  /**
   * Update a package for a venue
   * @param {string} venueId - The venue ID
   * @param {string} packageId - Package ID
   * @param {Object} packageData - Updated package payload
   * @returns {Promise<Object>} Updated package
   */
  async updateVenuePackage(venueId, packageId, packageData) {
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/packages/${packageId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders()
      },
      body: JSON.stringify(packageData)
    });
    if (!response.ok) {
      const errorMessage = await this.parseError(response);
      throw new Error(errorMessage);
    }
    return this.parseJsonBody(response);
  }

  /**
   * Delete a package
   * @param {string} venueId - The venue ID
   * @param {string} packageId - Package ID
   * @returns {Promise<void>}
   */
  async deleteVenuePackage(venueId, packageId) {
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/packages/${packageId}`, {
      method: 'DELETE',
      headers: {
        ...this.getAuthHeaders()
      }
    });
    if (!response.ok) {
      const errorMessage = await this.parseError(response);
      throw new Error(errorMessage);
    }
  }

  async getCalendarMaps(venueId) {
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/calendar-maps`, {
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() }
    });
    if (!response.ok) {
      if (response.status === 404) return [];
      throw new Error(await this.parseError(response));
    }
    const data = await response.json();
    return catalogArrayFromPayload(data, ['calendar_maps', 'maps', 'items']);
  }

  async createCalendarMap(venueId, mapPayload) {
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/calendar-maps`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify(mapPayload)
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }

  async deleteCalendarMap(venueId, mapId) {
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/calendar-maps/${mapId}`, {
      method: 'DELETE',
      headers: { ...this.getAuthHeaders() }
    });
    if (!response.ok) throw new Error(await this.parseError(response));
  }

  async setMultiSpaceCalendarsEnabled(venueId, enabled) {
    const response = await fetch(`${API_BASE_URL}/venues/${venueId}/multi-space-calendars`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
      body: JSON.stringify({ enabled: Boolean(enabled) })
    });
    if (!response.ok) throw new Error(await this.parseError(response));
    return response.json();
  }
}

// Export singleton instance
const venueDashboardApiService = new VenueDashboardApiService();
export default venueDashboardApiService;

